"use client";
import {
  AccountRecovery,
  type AccountRecoveryState,
} from "../account-recovery";
import { accountInitials } from "../initials";
import { Alert, Button, Text } from "@softmax/design-system";
import {
  colors,
  fonts,
  fontSizes,
  radii,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useRef } from "react";

const styles = stylex.create({
  row: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: spacing.space16,
  },
  image: {
    width: 72,
    height: 72,
    flexShrink: 0,
    borderRadius: radii.pill,
    objectFit: "cover",
  },
  initials: {
    width: 72,
    height: 72,
    display: "grid",
    placeItems: "center",
    borderRadius: radii.pill,
    backgroundColor: colors.selectedSurface,
    color: colors.selected,
    fontFamily: fonts.mono,
    fontSize: fontSizes.sectionTitle,
  },
  actions: { display: "grid", gap: spacing.space8 },
  buttons: { display: "flex", flexWrap: "wrap", gap: spacing.space8 },
  hidden: { display: "none" },
});

export function AccountAvatar({
  image,
  name,
  pending,
  error,
  disabled = false,
  recovery,
  onRefresh,
  onFile,
  onRemove,
}: {
  readonly image: string | null;
  readonly name: string;
  readonly pending: boolean;
  readonly error?: string;
  readonly disabled?: boolean;
  readonly recovery?: AccountRecoveryState;
  readonly onRefresh?: () => void;
  readonly onFile: (file: File) => void;
  readonly onRemove: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div {...stylex.props(styles.row)}>
      {image ? (
        <img src={image} alt={name} {...stylex.props(styles.image)} />
      ) : (
        <span aria-hidden {...stylex.props(styles.initials)}>
          {accountInitials(name)}
        </span>
      )}
      <div {...stylex.props(styles.actions)}>
        <div {...stylex.props(styles.buttons)}>
          <Button
            type="button"
            size="sm"
            disabled={pending || disabled || !!recovery}
            onClick={() => input.current?.click()}
          >
            {pending
              ? "Saving photo…"
              : image
                ? "Change photo"
                : "Upload photo"}
          </Button>
          {image ? (
            <Button
              type="button"
              size="sm"
              variant="quiet"
              disabled={pending || disabled || !!recovery}
              onClick={onRemove}
            >
              Remove photo
            </Button>
          ) : null}
        </div>
        <Text size="small" tone="muted">
          JPEG, PNG, WebP or GIF · up to 5 MB
        </Text>
        {error ? (
          <Alert tone="danger" announcement="assertive">
            {error}
          </Alert>
        ) : null}
        {recovery ? (
          <AccountRecovery
            recovery={recovery}
            resource="photo"
            onRefresh={onRefresh}
            size="sm"
          />
        ) : null}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        aria-label="Profile photo file"
        disabled={pending || disabled || !!recovery}
        onChange={(event) => {
          const file = event.target.files?.[0];

          if (file) onFile(file);

          event.target.value = "";
        }}
        {...stylex.props(styles.hidden)}
      />
    </div>
  );
}
